'use client';
import React, { useState } from 'react';
import { Cpu, Satellite, AlertTriangle, CheckCircle2, Clock, Play, RefreshCw, TrendingUp, Eye, Flag } from 'lucide-react';

interface AIDetection {
  id: string;
  type: 'unregistered_structure' | 'illegal_development' | 'property_expansion' | 'tax_evasion_zone';
  county: string;
  coordinates: string;
  confidence: number;
  detectedDate: string;
  status: 'new' | 'under_review' | 'confirmed' | 'dismissed';
  description: string;
  estimatedValue: string;
  satelliteRef: string;
}

const DETECTIONS: AIDetection[] = [
  { id: 'AI-2024-0312', type: 'unregistered_structure', county: 'Montserrado', coordinates: '6.318°N 10.801°W', confidence: 94, detectedDate: '24 May 2026', status: 'new', description: 'Multi-story commercial building detected with no parcel registration. Estimated 3,200 sqm footprint.', estimatedValue: '$280,000', satelliteRef: 'SAT-MON-20260524-0312' },
  { id: 'AI-2024-0287', type: 'property_expansion', county: 'Nimba', coordinates: '7.231°N 8.991°W', confidence: 88, detectedDate: '22 May 2026', status: 'under_review', description: 'Existing registered parcel LR-NIM-0008 expanded by ~0.4 ha without permit. New structures visible.', estimatedValue: '$45,000', satelliteRef: 'SAT-NIM-20260522-0287' },
  { id: 'AI-2024-0301', type: 'illegal_development', county: 'Margibi', coordinates: '6.531°N 10.352°W', confidence: 91, detectedDate: '21 May 2026', status: 'new', description: 'Industrial warehouse constructed on protected wetland zone. No environmental clearance on record.', estimatedValue: '$620,000', satelliteRef: 'SAT-MAR-20260521-0301' },
  { id: 'AI-2024-0255', type: 'tax_evasion_zone', county: 'Grand Bassa', coordinates: '5.882°N 10.049°W', confidence: 79, detectedDate: '18 May 2026', status: 'confirmed', description: 'Cluster of 12 commercial units operating without tax registration. High-density market activity detected.', estimatedValue: '$1,200,000', satelliteRef: 'SAT-GBS-20260518-0255' },
  { id: 'AI-2024-0244', type: 'unregistered_structure', county: 'Lofa', coordinates: '8.419°N 9.751°W', confidence: 85, detectedDate: '15 May 2026', status: 'under_review', description: 'Residential compound with 8 units. No ownership records. Possible informal settlement.', estimatedValue: '$95,000', satelliteRef: 'SAT-LOF-20260515-0244' },
  { id: 'AI-2024-0231', type: 'property_expansion', county: 'Bong', coordinates: '6.997°N 9.475°W', confidence: 82, detectedDate: '12 May 2026', status: 'confirmed', description: 'Agricultural land converted to commercial use. Registered as farmland but satellite shows warehouses.', estimatedValue: '$340,000', satelliteRef: 'SAT-BON-20260512-0231' },
  { id: 'AI-2024-0198', type: 'illegal_development', county: 'Maryland', coordinates: '4.378°N 7.719°W', confidence: 96, detectedDate: '08 May 2026', status: 'dismissed', description: 'Coastal construction flagged. After review, confirmed as licensed fishing infrastructure.', estimatedValue: '$0', satelliteRef: 'SAT-MRY-20260508-0198' },
];

const typeConfig = {
  unregistered_structure: { label: 'Unregistered Structure', color: '#f87171', icon: <AlertTriangle size={13} /> },
  illegal_development: { label: 'Illegal Development', color: '#fb923c', icon: <Flag size={13} /> },
  property_expansion: { label: 'Property Expansion', color: '#fbbf24', icon: <TrendingUp size={13} /> },
  tax_evasion_zone: { label: 'Tax Evasion Zone', color: '#e879f9', icon: <Eye size={13} /> },
};

const statusConfig = {
  new: { label: 'New', color: '#f87171', bg: 'rgba(248,113,113,0.15)', icon: <AlertTriangle size={11} /> },
  under_review: { label: 'Under Review', color: '#fbbf24', bg: 'rgba(251,191,36,0.15)', icon: <Clock size={11} /> },
  confirmed: { label: 'Confirmed', color: '#34d399', bg: 'rgba(52,211,153,0.15)', icon: <CheckCircle2 size={11} /> },
  dismissed: { label: 'Dismissed', color: '#94a3b8', bg: 'rgba(148,163,184,0.15)', icon: <CheckCircle2 size={11} /> },
};

export default function AIDiscoveryPanel() {
  const [running, setRunning] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCounty, setFilterCounty] = useState<string>('all');

  const handleRunScan = () => {
    setRunning(true);
    setTimeout(() => setRunning(false), 3000);
  };

  const filtered = DETECTIONS.filter((d) => {
    const matchStatus = filterStatus === 'all' || d.status === filterStatus;
    const matchCounty = filterCounty === 'all' || d.county === filterCounty;
    return matchStatus && matchCounty;
  });

  const counties = [...new Set(DETECTIONS.map((d) => d.county))];
  const newCount = DETECTIONS.filter((d) => d.status === 'new').length;
  const confirmedCount = DETECTIONS.filter((d) => d.status === 'confirmed').length;
  const totalValue = DETECTIONS.filter((d) => d.status !== 'dismissed').reduce((sum, d) => {
    const v = parseInt(d.estimatedValue.replace(/[$,]/g, '')) || 0;
    return sum + v;
  }, 0);

  return (
    <div className="space-y-4">
      {/* AI Scan header */}
      <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: running ? 'linear-gradient(135deg, #7c3aed, #4f46e5)' : 'rgba(212,160,23,0.15)', border: '1px solid rgba(212,160,23,0.3)' }}
            >
              <Cpu size={20} style={{ color: running ? '#fff' : 'var(--accent)', animation: running ? 'spin 1s linear infinite' : 'none' }} />
            </div>
            <div>
              <h2 className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>AI Property Discovery Engine</h2>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                {running ? '🔄 Scanning satellite imagery across all 15 counties…' : 'Satellite-powered detection of unregistered structures, illegal developments & tax evasion zones'}
              </p>
            </div>
          </div>
          <button
            onClick={handleRunScan}
            disabled={running}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 flex-shrink-0"
            style={{
              background: running ? 'rgba(124,58,237,0.2)' : 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)',
              color: running ? '#a78bfa' : '#070e1f',
              border: running ? '1px solid rgba(124,58,237,0.4)' : 'none',
              opacity: running ? 0.8 : 1,
            }}
          >
            {running ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
            {running ? 'Scanning…' : 'Run AI Scan'}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          {[
            { label: 'Total Detections', value: DETECTIONS.length.toString(), color: 'var(--dark-text)' },
            { label: 'New Alerts', value: newCount.toString(), color: '#f87171' },
            { label: 'Confirmed Cases', value: confirmedCount.toString(), color: '#34d399' },
            { label: 'Est. Taxable Value', value: `$${(totalValue / 1000000).toFixed(2)}M`, color: 'var(--accent)' },
          ].map((s) => (
            <div key={s.label} className="p-3 rounded-lg" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
              <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{s.label}</p>
              <p className="text-lg font-bold font-mono-data" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Filters + detections list */}
      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
        {/* Filter bar */}
        <div className="flex items-center gap-3 px-5 py-3 border-b flex-wrap" style={{ borderColor: 'var(--dark-border)' }}>
          <span className="text-xs font-semibold" style={{ color: 'var(--dark-muted)' }}>Filter:</span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-1.5 rounded-md text-xs outline-none"
            style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}
          >
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="under_review">Under Review</option>
            <option value="confirmed">Confirmed</option>
            <option value="dismissed">Dismissed</option>
          </select>
          <select
            value={filterCounty}
            onChange={(e) => setFilterCounty(e.target.value)}
            className="px-3 py-1.5 rounded-md text-xs outline-none"
            style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}
          >
            <option value="all">All Counties</option>
            {counties.map((c) => <option key={c}>{c}</option>)}
          </select>
          <span className="text-xs ml-auto" style={{ color: 'var(--dark-muted)' }}>{filtered.length} detections</span>
        </div>

        {/* Detection cards */}
        <div className="p-4 space-y-3">
          {filtered.map((detection) => {
            const tc = typeConfig[detection.type];
            const sc = statusConfig[detection.status];
            return (
              <div
                key={detection.id}
                className="p-4 rounded-lg"
                style={{ backgroundColor: 'var(--dark-elevated)', border: `1px solid ${detection.status === 'new' ? 'rgba(248,113,113,0.3)' : 'var(--dark-border)'}` }}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-start gap-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: `${tc.color}20`, color: tc.color }}
                    >
                      {tc.icon}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold font-mono-data" style={{ color: 'var(--accent)' }}>{detection.id}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: `${tc.color}20`, color: tc.color }}>
                          {tc.label}
                        </span>
                      </div>
                      <p className="text-sm font-medium" style={{ color: 'var(--dark-text)' }}>{detection.description}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: sc.bg, color: sc.color }}>
                      {sc.icon} {sc.label}
                    </span>
                    <div className="flex items-center gap-1">
                      <Satellite size={11} style={{ color: 'var(--dark-muted)' }} />
                      <span className="text-xs font-mono-data" style={{ color: 'var(--dark-muted)' }}>
                        {detection.confidence}% confidence
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
                  <div>
                    <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>County</p>
                    <p className="text-xs font-semibold" style={{ color: 'var(--dark-text)' }}>{detection.county}</p>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Coordinates</p>
                    <p className="text-xs font-mono-data" style={{ color: 'var(--dark-text)' }}>{detection.coordinates}</p>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Detected</p>
                    <p className="text-xs font-semibold" style={{ color: 'var(--dark-text)' }}>{detection.detectedDate}</p>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Est. Value</p>
                    <p className="text-xs font-bold font-mono-data" style={{ color: detection.estimatedValue === '$0' ? 'var(--dark-muted)' : 'var(--accent)' }}>{detection.estimatedValue}</p>
                  </div>
                </div>

                {/* Confidence bar */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: 'var(--dark-muted)' }}>AI Confidence</span>
                    <span className="font-mono-data font-bold" style={{ color: detection.confidence >= 90 ? '#34d399' : detection.confidence >= 75 ? '#fbbf24' : '#f87171' }}>
                      {detection.confidence}%
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--dark-border)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${detection.confidence}%`,
                        backgroundColor: detection.confidence >= 90 ? '#34d399' : detection.confidence >= 75 ? '#fbbf24' : '#f87171',
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
